import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import TiltCard from '../TiltCard';
import ScrollReveal from '../ScrollReveal';
import QuoteForm from '../QuoteForm';

// All pricing card text is white for maximum legibility
const getItemTextColor = () => 'text-white';

// Render a single price row
const PriceRow = ({ categoryId, item }) => (
  <div key={`${categoryId}-${item.service}`} className="flex justify-between items-start pb-4 border-b border-slate-700 last:border-0">
    <span className={`text-sm leading-relaxed pr-4 flex items-center gap-2 flex-wrap ${getItemTextColor()}`}>
      <span>{item.service}</span>
      {item.tag && (
        <span
          data-testid="pricing-item-tag"
          className={`text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded ${item.tag_color === 'gold' ? 'bg-yellow-400 text-yellow-950' : 'bg-red-600 text-white'}`}
        >
          {item.tag}
        </span>
      )}
    </span>
    <span className="text-white font-semibold text-sm whitespace-nowrap">
      {item.price}
    </span>
  </div>
);

// Group items by type so basic & advanced render as separate sub-sections in the card
const renderItems = (category) => {
  const basic = category.items.filter((i) => i.type === 'basic');
  const advanced = category.items.filter((i) => i.type === 'advanced');
  const other = category.items.filter((i) => i.type !== 'basic' && i.type !== 'advanced');

  // If no basic/advanced split exists, fall back to a flat list
  if (basic.length === 0 && advanced.length === 0) {
    return (
      <div className="space-y-4">
        {category.items.map((item) => (
          <PriceRow key={`${category.id}-${item.service}`} categoryId={category.id} item={item} />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pricing-split-card">
      {basic.length > 0 && (
        <div data-testid="pricing-group-basic">
          <h4 className="text-white text-sm font-bold uppercase tracking-wider mb-3 pb-2 border-b border-slate-600">
            Basic
          </h4>
          <div className="space-y-4">
            {basic.map((item) => (
              <PriceRow key={`${category.id}-${item.service}`} categoryId={category.id} item={item} />
            ))}
          </div>
        </div>
      )}
      {advanced.length > 0 && (
        <div data-testid="pricing-group-advanced">
          <h4 className="text-white text-sm font-bold uppercase tracking-wider mb-3 pb-2 border-b border-slate-600">
            Advanced
          </h4>
          <div className="space-y-4">
            {advanced.map((item) => (
              <PriceRow key={`${category.id}-${item.service}`} categoryId={category.id} item={item} />
            ))}
          </div>
        </div>
      )}
      {other.length > 0 && (
        <div className="space-y-4">
          {other.map((item) => (
            <PriceRow key={`${category.id}-${item.service}`} categoryId={category.id} item={item} />
          ))}
        </div>
      )}
    </div>
  );
};

const PricingSection = ({ pricingCategories, quoteDialogOpen, setQuoteDialogOpen, heading, subheading }) => {
  return (
    <section id="pricing" className="py-20 bg-slate-950/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {heading || 'Pricing'}
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              {subheading || 'Transparent pricing for quality workmanship'}
            </p>
          </div>
        </ScrollReveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {pricingCategories.map((category) => (
            <ScrollReveal key={category.id}>
              <TiltCard className="h-full">
                <Card className="bg-slate-800 border-slate-700 h-full">
                  <CardHeader>
                    <CardTitle className="text-white text-2xl text-center mb-4">
                      {category.category}
                    </CardTitle>
                    {category.description && (
                      <CardDescription className="text-white text-sm leading-relaxed text-center">
                        {category.description}
                      </CardDescription>
                    )}
                  </CardHeader>
                  <CardContent>
                    {renderItems(category)}
                  </CardContent>
                </Card>
              </TiltCard>
            </ScrollReveal>
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-gray-400 mb-6">
            Need a custom quote? Contact us for detailed pricing on your specific project.
          </p>
          <Dialog open={quoteDialogOpen} onOpenChange={setQuoteDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-base">
                Request Custom Quote
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-white">Request a Quote</DialogTitle>
              </DialogHeader>
              <QuoteForm onClose={() => setQuoteDialogOpen(false)} />
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
